<template>
  <div class="site-footer critical-component-clear">
    <div class="site-footer__middle-container">
      <div class="container grid">
        <div class="site-footer__content-cell col xs4 l4">
          <div
            v-if="liquid.showNewsletter"
            class="site-footer__newsletter"
          >
            <newsletter-form
              block-class="site-footer"
              :title="liquid.newsletterTitle"
              :subtitle="liquid.newsletterSubtitle"
              :disclaimer="liquid.newsletterDisclaimer"
              :modifiers="{
                title: 'text-heading-l-desktop',
                disclaimer: 'text-body-2xs-desktop',
                radio: 'radio--light'
              }"
              :list="liquid.list"
              title-element="h3"
            />

            <button
              v-if="liquid.cookieSetting"
              ref="cookieSetting"
              class="
                site-footer__cookie-setting
                text-body-2xs-link
                is-hidden
              "
              @click="openCookieSetting"
              v-text="liquid.cookieSetting"
            />

            <div
              v-if="liquid.showSocial"
              v-html="liquid.socialIcons"
            />
          </div>
        </div>

        <div class="site-footer__content-cell col xs4 l6-12">
          <div
            class="site-footer__nav"
            v-html="liquid.linkLists"
          />

          <div class="site-footer__accordions">
            <accordion :panel-data="liquid.panels" />
          </div>
        </div>
      </div>
    </div>

    <div class="site-footer__bottom-container">
      <div class="container site-footer__bottom">
        <!-- Logo and Copyright -->
        <div class="site-footer__copyright-wrapper">
          <a
            v-if="liquid.showLogo"
            class="site-footer__logo"
            :href="$variable('routes.root')"
            :aria-label="$string('general.site_footer.return_home')"
          >
            <div
              v-html="liquid.logo"
            />
          </a>
          <p
            class="text-body-2xs site-footer__copyright"
            v-html="liquid.copyright"
          />
        </div>

        <!-- Payment Icons -->
        <payment-icons
          v-if="liquid.showPaymentIcons"
          :payments="payments"
          :klarna-icon="liquid.showKlarnaIcon"
          :clearpay-icon="liquid.showClearpayIcon"
          :revolut-icon="liquid.showRevolutIcon"
        />

        <market-switcher-trigger
          class="site-footer__market-switcher"
          layout="large"
        />
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Site footer
 * -----------------------------------------------------------------------------
 * Site footer component with accordion display of links.
 *
 * @param {Object} liquid - Liquid data including strings, arrays and captured
 * content.
 * @param {Array} payments - Array of payment icon objects.
 * @param {String} shopName - Liquid name of the shop.
 *
 */
import { mapState } from 'vuex'

import Accordion from '~async/accordion/accordion'
import MarketSwitcherTrigger from '~async/market-switcher-trigger/market-switcher-trigger'
import NewsletterForm from '~async/newsletter-form/newsletter-form'
import PaymentIcons from '~async/payment-icons/payment-icons'

export default {
  name: 'SiteFooter',

  components: {
    Accordion,
    MarketSwitcherTrigger,
    NewsletterForm,
    PaymentIcons,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        cookieSetting: '',
        copyright: '',
        linkLists: '',
        list: '',
        logo: '',
        newsletterSubtitle: '',
        newsletterTitle: '',
        panels: [],
        showClearpayIcon: true,
        showKlarnaIcon: true,
        showLogo: true,
        showNewsletter: true,
        showPaymentIcons: true,
        showRevolutIcon: true,
        showSocial: true,
        socialIcons: '',
      }),
    },
    payments: {
      type: Array,
      default: () => [],
    },
    shopName: {
      type: String,
      default: '',
      required: true,
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      components: (state) => state.components,
    }),
  },

  watch: {

    /**
     * Watch components state to update sign posts.
     */
    components: {
      handler () {
        this.waitCookieBanner()
      },
      immediate: true,
    },
  },

  methods: {

    /**
     * Check cookie settings to show button after reload/apply/decline cookie.
     */
    checkCookieSetting() {
      if (window.OneTrust.IsAlertBoxClosedAndValid()) {
        this.$refs.cookieSetting.classList.remove(this.$class('hidden'))
      }
    },

    /**
     * Wait until OneTrust Cookie Banner loaded, then run event track function.
     * @return {Promise<void>}
     */
    async waitCookieBanner() {
      let elapsed = 0

      await new Promise((resolve) => {
        const cookieBannerInterval = window.setInterval(() => {
          if (!window.OneTrust || elapsed <= 3000) {
            elapsed += 100
            return
          }

          window.clearInterval(cookieBannerInterval)
          resolve()
        }, 20)
      })

      this.checkCookieSetting()
      this.checkCookieActions()
    },

    /**
     * Check any actions with cookie banner.
     */
    checkCookieActions() {
      window.OneTrust.OnConsentChanged(() => {
        this.$refs.cookieSetting.classList.remove(this.$class('hidden'))
      })
    },

    /**
     * Open cookie setting.
     */
    openCookieSetting() {
      window.OneTrust.ToggleInfoDisplay()
    },
  },
}
</script>

<style lang="scss">
@import './site-footer';
</style>
